import React, { useState, useEffect } from "react";
import {
  Button,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  FormGroup,
  Input,
  Col,
  Row,
  DropdownToggle,
  DropdownMenu,
  DropdownItem,
  UncontrolledDropdown,
} from "reactstrap";
import { useAuth } from "model/context/auth.context"; // Import useAuth
import { useFirebase } from "model/context/firebase.context";
import { COLLECTIONS } from "model/constants";

export function ProjectTransactionsModal({
  internal,
  openModal,
  setModalOpen,
  setError,
  projectId,
  transaction,
  editMode,
  setEditMode,
  setSelectedTransaction,
}) {
  const [loading, setLoading] = useState(false);
  const { session } = useAuth();
  const { db } = useFirebase();

  // Existing state variables
  const [transactionValue, setTransactionValue] = useState(0);
  const [transactionDate, setTransactionDate] = useState("");
  const [receiveOrPay, setReceiveOrPay] = useState(false);
  const [paid, setPaid] = useState(false);
  const [transactionMemo, setTransactionMemo] = useState("");

  // Add useEffect hook
  useEffect(() => {
    if (editMode && transaction) {
      // Populate form fields with transaction data
      setTransactionValue(transaction.transactionValue);
      setTransactionDate(transaction.transactionDate);
      setReceiveOrPay(transaction.receiveOrPay);
      setPaid(transaction.paid);
      setTransactionMemo(transaction.transactionMemo);
    } else {
      // Reset form fields when not editing
      resetState();
    }
  }, [editMode, transaction]);

  const resetState = () => {
    setTransactionValue(0);
    setTransactionDate("");
    setReceiveOrPay(false);
    setPaid(false);
    setTransactionMemo("");
  };

  const handleDropdownReceiveorPaySelect = (e, value) => {
    e.preventDefault();
    setReceiveOrPay(value);
  };
  const onSave = async () => {
    try {
      setLoading(true);

      // Debugging log to check inputs
      console.log("Transaction Data:", {
        transactionValue,
        transactionDate,
        receiveOrPay,
        paid,
        transactionMemo,
        projectId,
        transactionId: transaction?.id,
      });

      if (internal.current) {
        const updatedTransaction = {
          userName: session.user.displayName || session.user.email,
          createdAt:
            transaction && transaction.createdAt
              ? transaction.createdAt
              : new Date().toISOString(),
          transactionValue,
          transactionDate,
          receiveOrPay,
          paid,
          transactionMemo,
        };

        if (!projectId) {
          console.error("Error: Missing projectId");
          setError("Project ID is required to save the transaction.");
          return;
        }

        try {
          if (editMode && transaction) {
            // Update existing transaction
            await db.update(
              `${COLLECTIONS.projects}/${projectId}/transactions`,
              transaction.id,
              updatedTransaction
            );
            console.log("Transaction updated successfully.");
          } else {
            // Add new transaction
            await db.insert(
              `${COLLECTIONS.projects}/${projectId}/transactions`,
              updatedTransaction
            );
            console.log("Transaction added successfully.");
          }

          resetState();
          setModalOpen(false);
          setEditMode(false);
          setSelectedTransaction(null);
        } catch (e) {
          console.error("Error saving transaction:", e);
          setError(e.message || "Cannot save transaction due to an error");
        }
      } else {
        console.error("Error: Internal reference is missing.");
        setError("Internal reference is missing.");
      }
    } catch (e) {
      console.error("Error in onSave:", e);
      setError(e.message || "Cannot save transaction due to an error");
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Modal
        isOpen={openModal}
        toggle={() => {
          setModalOpen(false);
          setEditMode(false);
          setSelectedTransaction(null);
        }}
      >
        <ModalHeader>
          <h3>{editMode ? "Edit Transaction" : "Add Transaction"}</h3>
        </ModalHeader>
        <ModalBody>
          <Row>
            <Col md={6}>
              <FormGroup>
                <label className="form-control-label font-weight-light">
                  Transaction Value
                </label>
                <Input
                  required
                  name="Transaction Value"
                  placeholder="Transaction"
                  type="number"
                  value={transactionValue}
                  onChange={(e) => setTransactionValue(e.target.value)}
                />
              </FormGroup>
            </Col>
            <Col md={6}>
              <FormGroup>
                <label className="form-control-label font-weight-light">
                  Transaction Date
                </label>
                <Input
                  required
                  name="TransactionDate"
                  placeholder="Transaction Date"
                  type="date"
                  value={transactionDate}
                  onChange={(e) => setTransactionDate(e.target.value)}
                />
              </FormGroup>
            </Col>
          </Row>
          <Row>
            <Col md={6}>
              <FormGroup>
                <label className="form-control-label font-weight-light">
                  Receive or Pay
                </label>
                <br></br>
                <Input
                  hidden
                  name="ReceiveorPay"
                  className="form-control text-dark"
                  placeholder="Receive or Pay"
                  value={receiveOrPay ? "Receive" : "Pay"}
                  readOnly
                />
                <UncontrolledDropdown>
                  <DropdownToggle caret color="primary">
                    {receiveOrPay ? "Receive" : "Pay"}
                  </DropdownToggle>
                  <DropdownMenu>
                    <DropdownItem
                      href="#"
                      onClick={(e) => handleDropdownReceiveorPaySelect(e, true)}
                    >
                      Receive
                    </DropdownItem>
                    <DropdownItem
                      href="#"
                      onClick={(e) =>
                        handleDropdownReceiveorPaySelect(e, false)
                      }
                    >
                      Pay
                    </DropdownItem>
                  </DropdownMenu>
                </UncontrolledDropdown>
              </FormGroup>
            </Col>
            <Col md={6}>
              <FormGroup>
                <div className="d-flex align-items-center">
                  <small className="form-control-label font-weight-light mr-3">
                    Paid?
                  </small>
                  <div>
                    <label className="custom-toggle">
                      <input
                        type="checkbox"
                        checked={paid}
                        onChange={(e) => setPaid(e.target.checked)}
                      />
                      <span
                        className="custom-toggle-slider rounded-circle"
                        data-label-off="No"
                        data-label-on="Yes"
                      />
                    </label>
                  </div>
                </div>
              </FormGroup>
            </Col>
          </Row>
          <Row>
            <Col md={12}>
              <FormGroup>
                <label className="form-control-label font-weight-light">
                  Transaction Memo
                </label>
                <Input
                  required
                  name="Transaction Memo"
                  placeholder="Transaction Memo"
                  type="textarea"
                  value={transactionMemo}
                  onChange={(e) => setTransactionMemo(e.target.value)}
                />
              </FormGroup>
            </Col>
          </Row>
        </ModalBody>
        <ModalFooter>
          <Button
            color="light"
            type="button"
            onClick={() => {
              setModalOpen(false);
              setEditMode(false);
              setSelectedTransaction(null);
            }}
          >
            Cancel
          </Button>

          <Button
            className="ml-4"
            color="danger"
            type="button"
            onClick={onSave}
            disabled={loading}
          >
            {editMode ? "Update" : "Save"}
          </Button>
        </ModalFooter>
      </Modal>
    </>
  );
}
